import type {
  PersonnelCostCategory,
  PersonnelCostDepartment,
  PersonnelCostEmployeeSettings,
  PersonnelCostEntry,
  PersonnelCostValueKind,
} from "@/lib/personnelCosts";
import { personnelDepartmentLabel } from "@/lib/personnelCosts";

export type CalcMode = "forward" | "reverse";

export type CostLineKind =
  | "gross"
  | "percent"
  | "fixed_employee"
  | "fixed_global"
  | "fixed_department"
  | "hourly";

export interface CostBreakdownLine {
  categoryId: number | null;
  label: string;
  amountMonthly: number;
  kind: CostLineKind;
  valueKind?: PersonnelCostValueKind;
}

export interface EmployeeCostContext {
  login: string;
  department: PersonnelCostDepartment | null;
}

export interface PersonnelCostCalcInput {
  grossMonthly: number;
  weeklyHours: number;
  vacationDays: number;
  bridgeHolidaysDays: number;
  categories: PersonnelCostCategory[];
  allEntries: PersonnelCostEntry[];
  userLogin: string;
  employeeDepartment: PersonnelCostDepartment | null;
  employees: EmployeeCostContext[];
  mode: CalcMode;
  targetHourlyRate?: number;
}

export interface PersonnelCostCalcResult {
  lines: CostBreakdownLine[];
  grossMonthly: number;
  totalMonthly: number;
  hourlyRate: number;
  productiveHoursYear: number;
  requiredGrossMonthly: number | null;
}

export function productiveHoursPerYear(
  weeklyHours: number,
  vacationDays: number,
  bridgeHolidaysDays: number,
): number {
  const dailyHours = weeklyHours / 5;
  const workDays = 52 * 5 - vacationDays - bridgeHolidaysDays;
  return Math.max(0, workDays * dailyHours);
}

function normalizeLogin(value: string): string {
  return value.toLowerCase().split("@")[0] || value;
}

function employeeMatchesDepartments(
  department: PersonnelCostDepartment | null,
  departments: PersonnelCostDepartment[] | null | undefined,
): boolean {
  if (!departments?.length) return true;
  return department != null && departments.includes(department);
}

function entryAppliesToEmployee(
  entry: PersonnelCostEntry,
  login: string,
  department: PersonnelCostDepartment | null,
): boolean {
  if (entry.scope === "employee") {
    return normalizeLogin(entry.user_login ?? "") === normalizeLogin(login);
  }
  if (entry.scope === "department") {
    return employeeMatchesDepartments(department, entry.departments);
  }
  if (entry.scope === "global") {
    return employeeMatchesDepartments(department, entry.departments);
  }
  return false;
}

function countGlobalSplitTargets(
  entry: PersonnelCostEntry,
  employees: EmployeeCostContext[],
): number {
  if (!entry.departments?.length) {
    return Math.max(1, employees.length);
  }
  return Math.max(
    1,
    employees.filter(
      (emp) => emp.department && entry.departments!.includes(emp.department),
    ).length,
  );
}

function resolvePercent(
  category: PersonnelCostCategory,
  entry: PersonnelCostEntry | undefined,
): number {
  if (entry?.percent_value != null) return Number(entry.percent_value) || 0;
  return Number(category.default_percent) || 0;
}

function buildEntryLabel(category: PersonnelCostCategory, entry: PersonnelCostEntry): string {
  const deptLabel =
    entry.departments?.map((d) => personnelDepartmentLabel(d)).join(", ") ?? "";
  if (entry.scope === "employee") return `${category.label} (MA)`;
  if (entry.scope === "department") {
    return `${category.label} (${deptLabel || "Abteilung"})`;
  }
  if (entry.departments?.length) {
    return `${category.label} (Anteil ${deptLabel})`;
  }
  return `${category.label} (Anteil)`;
}

export function buildCostBreakdown(input: PersonnelCostCalcInput): PersonnelCostCalcResult {
  const productiveHoursYear = productiveHoursPerYear(
    input.weeklyHours,
    input.vacationDays,
    input.bridgeHolidaysDays,
  );

  const login = normalizeLogin(input.userLogin);
  const grossMonthly = Math.max(0, Number(input.grossMonthly) || 0);
  const lines: CostBreakdownLine[] = [
    {
      categoryId: null,
      label: "Bruttogehalt",
      amountMonthly: grossMonthly,
      kind: "gross",
    },
  ];

  let percentSum = 0;
  let fixedTotal = 0;

  for (const category of input.categories) {
    const categoryEntries = input.allEntries.filter((entry) => entry.category_id === category.id);

    for (const entry of categoryEntries) {
      if (!entryAppliesToEmployee(entry, login, input.employeeDepartment)) continue;

      if (category.value_kind === "percent_gross") {
        const percent = resolvePercent(category, entry);
        if (percent <= 0) continue;
        const amount = (grossMonthly * percent) / 100;
        percentSum += amount;
        lines.push({
          categoryId: category.id,
          label: buildEntryLabel(category, entry),
          amountMonthly: amount,
          kind: "percent",
          valueKind: category.value_kind,
        });
        continue;
      }

      if (category.value_kind === "monthly_eur") {
        let amount = 0;
        let kind: CostLineKind = "fixed_employee";
        if (entry.scope === "global") {
          amount =
            (Number(entry.amount_monthly) || 0) /
            countGlobalSplitTargets(entry, input.employees);
          kind = "fixed_global";
        } else if (entry.scope === "department") {
          amount = Number(entry.amount_monthly) || 0;
          kind = "fixed_department";
        } else {
          amount = Number(entry.amount_monthly) || 0;
          kind = "fixed_employee";
        }
        if (amount <= 0) continue;
        fixedTotal += amount;
        lines.push({
          categoryId: category.id,
          label: buildEntryLabel(category, entry),
          amountMonthly: amount,
          kind,
          valueKind: category.value_kind,
        });
        continue;
      }

      if (category.value_kind === "hourly_eur") {
        const hourly = Number(entry.hourly_amount) || 0;
        if (hourly <= 0) continue;
        let amount = hourly * (productiveHoursYear / 12);
        let kind: CostLineKind = "hourly";
        if (entry.scope === "global") {
          amount /= countGlobalSplitTargets(entry, input.employees);
          kind = "fixed_global";
        } else if (entry.scope === "department") {
          kind = "fixed_department";
        }
        fixedTotal += amount;
        lines.push({
          categoryId: category.id,
          label: buildEntryLabel(category, entry),
          amountMonthly: amount,
          kind,
          valueKind: category.value_kind,
        });
      }
    }
  }

  const totalMonthly = grossMonthly + percentSum + fixedTotal;
  const hourlyRate =
    productiveHoursYear > 0 ? (totalMonthly * 12) / productiveHoursYear : 0;

  let requiredGrossMonthly: number | null = null;
  if (input.mode === "reverse" && input.targetHourlyRate != null && productiveHoursYear > 0) {
    const targetMonthly = (input.targetHourlyRate * productiveHoursYear) / 12;
    let percentRateSum = 0;
    for (const category of input.categories) {
      if (category.value_kind !== "percent_gross") continue;
      const categoryEntries = input.allEntries.filter((entry) => entry.category_id === category.id);
      for (const entry of categoryEntries) {
        if (!entryAppliesToEmployee(entry, login, input.employeeDepartment)) continue;
        percentRateSum += resolvePercent(category, entry);
      }
    }
    const divisor = 1 + percentRateSum / 100;
    requiredGrossMonthly = divisor > 0 ? Math.max(0, (targetMonthly - fixedTotal) / divisor) : null;
  }

  return {
    lines,
    grossMonthly,
    totalMonthly,
    hourlyRate,
    productiveHoursYear,
    requiredGrossMonthly,
  };
}

export function buildEmployeeCostContexts(
  employees: Array<{ login: string }>,
  settings: PersonnelCostEmployeeSettings[],
): EmployeeCostContext[] {
  return employees.map((emp) => {
    const normalized = normalizeLogin(emp.login);
    const found = settings.find((s) => normalizeLogin(s.user_login) === normalized);
    return {
      login: normalized,
      department: found?.department ?? null,
    };
  });
}

export function calcForEmployee(params: {
  login: string;
  settings: PersonnelCostEmployeeSettings;
  categories: PersonnelCostCategory[];
  allEntries: PersonnelCostEntry[];
  employees: EmployeeCostContext[];
  mode?: CalcMode;
  targetHourlyRate?: number;
}): PersonnelCostCalcResult {
  const mode = params.mode ?? "forward";
  const target =
    mode === "reverse"
      ? (params.targetHourlyRate ?? params.settings.target_hourly_rate ?? undefined)
      : undefined;

  const baseInput = {
    weeklyHours: params.settings.weekly_hours,
    vacationDays: params.settings.vacation_days,
    bridgeHolidaysDays: params.settings.bridge_holidays_days,
    categories: params.categories,
    allEntries: params.allEntries,
    userLogin: params.login,
    employeeDepartment: params.settings.department,
    employees: params.employees,
  };

  const forward = buildCostBreakdown({
    ...baseInput,
    grossMonthly: params.settings.gross_monthly,
    mode: "forward",
  });

  if (mode !== "reverse" || target == null) {
    return forward;
  }

  const reverse = buildCostBreakdown({
    ...baseInput,
    grossMonthly: 0,
    mode: "reverse",
    targetHourlyRate: target,
  });

  const requiredGross = reverse.requiredGrossMonthly ?? 0;
  const withRequiredGross = buildCostBreakdown({
    ...baseInput,
    grossMonthly: requiredGross,
    mode: "forward",
  });

  return {
    ...withRequiredGross,
    requiredGrossMonthly: requiredGross,
    hourlyRate: target,
  };
}
